import { useState } from "react";

import AddIcon from "@mui/icons-material/AddRounded";
import MergeIcon from "@mui/icons-material/MergeRounded";
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";

import { DataTable } from "@/common/components/table/DataTable";
import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import withAuthorization from "@/common/hocs/with-authorization/withAuthorization";
import { useTranslation } from "@/translations";

import { ParticipantCreationDialog } from "./components/create-participant/participant-creation-dialog/ParticipantCreationDialog";
import { ParticipantEditDialog } from "./components/edit-participant/participant-edit-dialog/ParticipantEditDialog";
import { MergeSelectionBar } from "./components/merge-mode/MergeSelectionBar";
import { MergeParticipantsDialog } from "./components/merge-mode/merge-participants-dialog/MergeParticipantsDialog";
import { ParticipantsSearch } from "./components/search/ParticipantsSearch";
import { useMergeMode } from "./hooks/useMergeMode";
import { useProceedingParticipants } from "./hooks/useProceedingParticipants";

function ProceedingParticipantsInner() {
  const { t } = useTranslation();
  const {
    participants,
    hasAdditionalParticipants,
    isLoading,
    columnConfigurations,
    searchText,
    setSearchText,
    showInactive,
    setShowInactive,
  } = useProceedingParticipants();

  const {
    isMergeMode,
    selectionModel,
    onSelectionChange,
    selectedParticipants,
    hasMixedTypes,
    isValidSelection,
    enterMergeMode,
    exitMergeMode,
    clearSelection,
  } = useMergeMode(participants);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);

  return (
    <Stack spacing={1} sx={{ height: "100%" }} overflow={"hidden"}>
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box flex={1} minWidth={0}>
            <ParticipantsSearch initialValue={searchText} onChange={setSearchText} width="100%" />
          </Box>
          <FormControlLabel
            control={<Checkbox checked={showInactive} onChange={(_, checked) => setShowInactive(checked)} />}
            label={t("proceedingParticipants.showInactive")}
          />
        </Box>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant={isMergeMode ? "contained" : "outlined"}
            startIcon={<MergeIcon />}
            onClick={isMergeMode ? exitMergeMode : enterMergeMode}
          >
            {t(isMergeMode ? "proceedingParticipants.merge.endButton" : "proceedingParticipants.merge.button")}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateDialogOpen(true)}>
            {t("proceedingParticipants.createParticipant")}
          </Button>
        </Stack>
      </Stack>
      {isMergeMode && (
        <MergeSelectionBar
          selectedCount={selectedParticipants.length}
          isValidSelection={isValidSelection}
          hasMixedTypes={hasMixedTypes}
          onClearSelection={clearSelection}
          onStartMerge={() => setIsMergeDialogOpen(true)}
        />
      )}
      {!isLoading && hasAdditionalParticipants && (
        <Typography variant="subtitle1">{t("proceedingParticipants.moreResults")}</Typography>
      )}
      {participants.length === 0 && !isLoading ? (
        <Typography sx={{ whiteSpace: "pre-wrap" }}>{t("proceedingParticipants.noResults")}</Typography>
      ) : (
        <Stack
          sx={{
            overflow: "auto",
            flex: 1,
            minHeight: 0,
          }}
        >
          <DataTable
            data={participants}
            columns={columnConfigurations}
            getRowId={(row) => row.proceedingParticipantId}
            onRowClick={(id) => setSelectedParticipantId(id)}
            checkboxSelection={isMergeMode}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={onSelectionChange}
            hideSelectAllCheckbox
            paginationResetKey={`${searchText}|${showInactive}`}
          />
        </Stack>
      )}
      {selectedParticipantId && (
        <ParticipantEditDialog participantId={selectedParticipantId} onClose={() => setSelectedParticipantId(null)} />
      )}

      {isCreateDialogOpen && <ParticipantCreationDialog onClose={() => setIsCreateDialogOpen(false)} />}

      {isMergeDialogOpen && (
        <MergeParticipantsDialog
          selectedParticipants={selectedParticipants}
          onClose={() => setIsMergeDialogOpen(false)}
          onMerged={exitMergeMode}
        />
      )}
    </Stack>
  );
}

export const ProceedingParticipants = withAuthorization(ProceedingParticipantsInner, UserPermission.Supervisor);
